<script lang="ts">
  import { goto } from "$app/navigation";
  import { page } from "$app/stores";
  import { createAuthStore, AuthState } from "$lib/stores/authStore.svelte";
  import { appStore } from "$lib/stores/appStore.svelte";
  import { signOutUser } from "$lib/firebase/auth";
  import { t as __t, getLocale, onLocaleChange } from "$lib/i18n";
  import { isTauri, isOnline, onOnlineChange } from "$lib/platform/detector";
  import HamburgerMenu from "./HamburgerMenu.svelte";
  import BottomTabBar from "./BottomTabBar.svelte";
  import FloatingCommandBar from "./Admin/FloatingCommandBar.svelte";
  import CookieConsent from "./CookieConsent.svelte";
  import TutorialOverlay from "./TutorialOverlay.svelte";

  let {
    children,
    initializing = false,
  }: { children: import("svelte").Snippet; initializing?: boolean } = $props();

  // Offline indicator for Tauri
  let _isOnline = $state(true);
  $effect(() => {
    const unsub = onOnlineChange((v) => (_isOnline = v));
    return unsub;
  });
  let showOfflineBanner = $derived(isTauri() && !_isOnline);

  // Auth store
  const authStore = createAuthStore();
  let path = $derived($page.url.pathname);

  // Reactive locale for translations
  let _lcv = $state(0);
  let currentLocale = $state(getLocale());
  $effect(() => {
    const unsub = onLocaleChange(() => {
      currentLocale = getLocale();
      _lcv++;
    });
    return unsub;
  });
  function _t(key: string, params?: Record<string, string | number>): string {
    _lcv;
    return __t(key, params);
  }

  // Derived auth state
  let user = $derived(authStore.user as any);
  let loggedIn = $derived(authStore.state === AuthState.AUTHENTICATED);
  let hideNavbar = $derived(appStore.hideNavbar);
  let hasInvites = $derived(
    user?.voicePlans?.some(
      (vp: any) => vp.invited === true && vp.accepted !== true,
    ) ?? false,
  );
  let hamburgerOpen = $state(false);

  // Scroll-aware navbar background
  let scrollY = $state(0);
  let sectionEl = $state<HTMLElement | undefined>(undefined);
  let scrollContainer = $state<HTMLElement | undefined>(undefined);
  let logoEl = $state<HTMLElement | undefined>(undefined);
  let navElement = $state<HTMLElement | undefined>(undefined);
  let fixedLogoStyle = $state("");
  let hideOriginalLogo = $state(false);

  let scrolledPastHeader = $derived(
    sectionEl ? scrollY > sectionEl.offsetHeight * 0.5 : false,
  );

  $effect(() => {
    if (typeof window === "undefined") return;
    const el: HTMLElement | undefined = scrollContainer;
    if (!el) return;

    let frameId: number | null = null;
    const scheduleUpdate = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(() => {
          frameId = null;
          updateLogo();
        });
      }
    };

    const updateLogo = () => {
      scrollY = el.scrollTop;

      // Calculate and apply scrollbar width to prevent fixed navbar from going under it
      const scrollbarWidth = el.offsetWidth - el.clientWidth;
      if (
        navElement &&
        navElement.style.paddingRight !== `${scrollbarWidth}px`
      ) {
        navElement.style.paddingRight = `${scrollbarWidth}px`;
      }

      if (!sectionEl || !logoEl || !navElement || hideNavbar || !el) {
        hideOriginalLogo = false;
        fixedLogoStyle = "";
        return;
      }

      const scrollYVal = el.scrollTop;
      const heroHeight = sectionEl.offsetHeight;
      const scrollThreshold = heroHeight * 0.3; // Start animation when 30% scrolled
      const animationDistance = heroHeight * 0.5; // Animation completes over 50% of hero height

      const logoRect = logoEl.getBoundingClientRect();
      const navRect = navElement.getBoundingClientRect();

      // Calculate centers
      const startCenterX = logoRect.left + logoRect.width / 2;
      const startCenterY = logoRect.top + logoRect.height / 2;
      const startScale = 1.0;

      // Center relative to content area rather than full viewport width
      const targetCenterX = (window.innerWidth - scrollbarWidth) / 2;
      const targetCenterY = navRect.bottom - 30; // Center of 60px navbar content area
      const targetScale = 0.4;

      let currentCenterX = startCenterX;
      let currentCenterY = startCenterY;
      let currentScale = startScale;
      let progress = 0;

      if (scrollYVal > scrollThreshold) {
        progress = Math.min(
          (scrollYVal - scrollThreshold) / animationDistance,
          1,
        );
        currentCenterX =
          startCenterX + (targetCenterX - startCenterX) * progress;
        currentCenterY =
          startCenterY + (targetCenterY - startCenterY) * progress;
        currentScale = startScale + (targetScale - startScale) * progress;
      }

      const currentBrightness = 1 - progress;
      const currentInvert = progress;

      // We use absolute width of 200px and height of 112.5px (16:9 ratio)
      const w = 200;
      const h = 112.5;

      hideOriginalLogo = true;
      fixedLogoStyle = `
        position: fixed;
        top: 0;
        left: 0;
        width: ${w}px;
        height: ${h}px;
        transform: translate(${currentCenterX - w / 2}px, ${currentCenterY - h / 2}px) scale(${currentScale});
        transform-origin: center center;
        z-index: 10;
        filter: brightness(${currentBrightness}) invert(${currentInvert});
        cursor: pointer;
        pointer-events: auto;
      `;
    };

    const handleScroll = () => {
      scheduleUpdate();
    };

    const handleResize = () => {
      scheduleUpdate();
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    // Use ResizeObserver to detect layout/scrollbar changes dynamically (e.g. when content loads)
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(el);

    // Initial call
    scheduleUpdate();

    // Setup a small timeout to ensure initial layout runs after DOM settlement
    const timer = setTimeout(scheduleUpdate, 100);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
      clearTimeout(timer);
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  });

  async function handleLogout() {
    await signOutUser();
  }

  function handleBack() {
    history.back();
  }

  function handleHome() {
    goto("/");
  }
</script>

<!-- Bottom Tab Bar -->
{#if loggedIn && !hideNavbar && path !== "/"}
  <BottomTabBar {user} />
{/if}

<!-- App Shell — fills viewport, owns all scrolling -->
<div class="h-dvh flex flex-col">
  {#if authStore.isImpersonating}
    <div
      class="bg-blue-600 text-white text-center text-sm py-2 font-medium flex items-center justify-center gap-3 flex-shrink-0 z-50"
    >
      <span
        >{_t("admin.impersonating", {
          name: user?.name || user?.email || user?.uid,
        })}</span
      >
      <button
        onclick={() => authStore.stopImpersonating()}
        class="bg-white text-blue-600 px-3 py-1 rounded text-xs font-bold hover:bg-gray-100 transition-colors"
      >
        {_t("admin.stop-impersonation")}
      </button>
    </div>
  {/if}
  {#if showOfflineBanner}
    <div
      class="bg-amber-500 text-white text-center text-xs py-1 font-medium flex-shrink-0"
    >
      Offline mode — some features may be unavailable
    </div>
  {/if}
  <div
    class="flex-1 overflow-y-auto overscroll-contain"
    data-scroll-container
    bind:this={scrollContainer}
  >
    <!-- Hero / Header Section -->
    <section
      class="bg-black bg-[url('/images/header.webp')] bg-cover bg-center flex-shrink-0"
      class:hidden={hideNavbar}
      bind:this={sectionEl}
    >
      <div class="hero-body">
        <!-- Fixed Top Navbar -->
        <nav
          bind:this={navElement}
          class="fixed top-0 left-0 right-0 flex items-end z-40 safe-area-top transition-colors duration-300 {scrolledPastHeader
            ? 'bg-black'
            : 'bg-black/0'}"
          aria-label="main navigation"
          class:hidden={hideNavbar || initializing}
        >
          <!-- Fixed Position Logo for Seamless Scroll Animation -->
          {#if hideOriginalLogo && fixedLogoStyle}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <div
              class="fixed-logo"
              style={fixedLogoStyle}
              onclick={initializing ? undefined : handleHome}
            >
              <figure class="aspect-[16/9] w-full">
                <img
                  src="/images/uvi-logo-large.png"
                  alt="Universal Voice Logo"
                  class="w-full h-auto"
                />
              </figure>
            </div>
          {/if}

          <div class="flex items-center h-[60px] flex-1 min-w-0 relative">
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <span
              class="inline-flex items-center h-[60px] px-2 md:px-3 cursor-pointer rounded transition-colors duration-300 {scrolledPastHeader
                ? 'bg-black'
                : 'bg-black/30'}"
              onclick={handleBack}
            >
              <i class="fas fa-angle-left text-white text-lg md:text-xl"></i>
            </span>
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <span
              class="inline-flex items-center h-[60px] px-2 md:px-3 cursor-pointer rounded transition-colors duration-300 {scrolledPastHeader
                ? 'bg-black'
                : 'bg-black/30'}"
              onclick={handleHome}
            >
              <i class="fas fa-home text-white text-lg md:text-xl"></i>
            </span>

            <span class="flex-1"></span>

            {#if hasInvites}
              <!-- svelte-ignore a11y_click_events_have_key_events -->
              <span
                class="inline-flex items-center h-[60px] px-2 md:px-3 cursor-pointer"
                onclick={() => goto("/invitations")}
              >
                <span
                  class="bg-primary-500 text-white text-xs font-bold px-2 py-0.5 rounded"
                >
                  New invitations!
                </span>
              </span>
            {/if}
          </div>

          <div class="h-[60px] flex items-center shrink-0">
            <HamburgerMenu
              {user}
              {loggedIn}
              onLogout={handleLogout}
              bind:isOpen={hamburgerOpen}
            />
          </div>
        </nav>

        <!-- Logo -->
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <div
          bind:this={logoEl}
          class="w-[200px] mx-auto cursor-pointer pt-16 md:pt-20 pb-12 md:pb-36"
          style="opacity: {hideOriginalLogo ? 0 : 1};"
          onclick={initializing ? undefined : handleHome}
        >
          <figure class="aspect-[16/9] w-full">
            <img
              src="/images/uvi-logo-large.png"
              alt="Universal Voice Logo"
              class="w-full h-auto"
            />
          </figure>
        </div>
      </div>
    </section>

    <!-- Main Content Area -->
    <main
      class="w-full text-xs md:text-base {hideNavbar ? '' : 'max-w-[1000px] mx-auto px-4 pb-[120px] pt-5'}"
    >
      {@render children()}
    </main>
  </div>
</div>

<!-- Floating command bar for admin users -->
{#if user?.hasFeature?.("ADMIN")}
  <FloatingCommandBar />
{/if}

<!-- Cookie consent for unauthenticated users -->
{#if !loggedIn && !initializing}
  <CookieConsent showButton={false} />
{/if}

{#if !initializing}
  <TutorialOverlay />
{/if}

<style>
  .hero-body {
    padding: 0;
  }

  section.hidden {
    display: none;
  }

  nav.hidden {
    display: none;
  }

  .safe-area-top {
    padding-top: env(safe-area-inset-top, 0px);
  }

  .fixed-logo {
    margin: 0 auto;
    pointer-events: auto;
  }
</style>
